import type { ContentType, UrlEntity } from './noteTypes'

export type UrlContentCategory =
  | 'movie'
  | 'audio'
  | 'video'
  | 'social'
  | 'reference'
  | 'book'
  | 'event'
  | 'document'
  | 'article'
  | 'product'
  | 'place'
  | 'code'
  | 'listing'
  | 'recipe'
  | 'job'
  | 'link'

/** Categories a page can declare for itself; `link` means "nothing more specific known". */
export type PageKind = Exclude<UrlContentCategory, 'link'>

export const PAGE_KINDS: readonly PageKind[] = [
  'movie', 'audio', 'video', 'social', 'reference', 'book', 'event', 'document',
  'article', 'product', 'place', 'code', 'listing', 'recipe', 'job',
]

export function isPageKind(value: unknown): value is PageKind {
  return typeof value === 'string' && (PAGE_KINDS as readonly string[]).includes(value)
}

const MOVIE_HOSTS = ['imdb.com', 'letterboxd.com', 'rottentomatoes.com', 'themoviedb.org'] as const
const AUDIO_HOSTS = ['spotify.com', 'music.apple.com', 'soundcloud.com', 'bandcamp.com', 'podcasts.apple.com'] as const
const VIDEO_HOSTS = ['youtube.com', 'youtu.be', 'vimeo.com', 'tiktok.com', 'twitch.tv'] as const
const SOCIAL_HOSTS = ['instagram.com', 'x.com', 'twitter.com', 'threads.net', 'bsky.app', 'reddit.com', 'linkedin.com', 'facebook.com'] as const
const REFERENCE_HOSTS = ['wikipedia.org', 'wikimedia.org', 'britannica.com'] as const
const BOOK_HOSTS = ['goodreads.com'] as const
const EVENT_HOSTS = ['meetup.com', 'meetu.ps'] as const
const CODE_HOSTS = ['github.com', 'gitlab.com', 'bitbucket.org', 'codeberg.org'] as const
const PLACE_HOSTS = ['openstreetmap.org', 'airbnb.com'] as const
/** Sites whose pages are recipes, for the many recipe pages that declare nothing about themselves. */
const RECIPE_HOSTS = [
  'allrecipes.com', 'seriouseats.com', 'bbcgoodfood.com', 'cooking.nytimes.com', 'epicurious.com', 'food52.com', 'bonappetit.com',
  'smittenkitchen.com', 'budgetbytes.com', 'simplyrecipes.com', 'sallysbakingaddiction.com', 'kingarthurbaking.com', 'minimalistbaker.com',
  'cookieandkate.com', 'ambitiouskitchen.com', 'chefkoch.de', 'lecker.de', 'essen-und-trinken.de', 'marmiton.org', 'giallozafferano.it',
  'hashulchan.co.il', '10dakot.co.il', 'foodish.co.il',
] as const
/** "Recipe" in the languages the owners save in, as a whole word of a title or an address; Hebrew may carry a one or two letter prefix (המתכון). */
const RECIPE_WORD = /^(?:recipes?|rezepte?|recetas?|recettes?|ricett[ae]|receitas?|[הוכלבשמ]{0,2}מתכו(?:ן|נים|ני))$/u
/** The idiom, not a dish. */
const FIGURATIVE_RECIPE = /\brecipe for (?:success|disaster|happiness)\b/i
const AMAZON_SHORT_HOSTS = ['a.co', 'amzn.to', 'amzn.eu', 'amzn.asia'] as const
const AMAZON_STOREFRONT = /^amazon\.(?:com|(?:com|co)\.[a-z]{2}|[a-z]{2})$/

/**
 * Recognized map links. Keep short-share signatures here so classification works
 * before a redirect is resolved, and add new provider formats as distinct entries.
 */
export interface MapUrlPattern {
  readonly provider: 'Google Maps' | 'Apple Maps' | 'Waze' | 'OpenStreetMap' | 'Bing Maps'
  readonly description: string
  readonly hosts?: readonly string[]
  readonly pathname?: RegExp
  readonly protocols?: readonly string[]
}

export const MAP_URL_PATTERNS: readonly MapUrlPattern[] = [
  {
    provider: 'Google Maps',
    description: 'Current Google Maps share links',
    hosts: ['maps.app.goo.gl'],
  },
  {
    provider: 'Google Maps',
    description: 'Legacy Google Maps short links',
    hosts: ['goo.gl'],
    pathname: /^\/maps(?:\/|$)/,
  },
  {
    provider: 'Google Maps',
    description: 'Google share and Firebase Dynamic Link map redirects',
    hosts: ['share.google', 'maps.page.link'],
  },
  {
    provider: 'Google Maps',
    description: 'Full Google Maps URLs',
    hosts: ['maps.google.com'],
  },
  {
    provider: 'Google Maps',
    description: 'Google Maps paths on Google domains',
    hosts: ['google.com'],
    pathname: /^\/maps(?:\/|$)/,
  },
  {
    provider: 'Apple Maps',
    description: 'Apple Maps short and full share links',
    hosts: ['maps.apple', 'maps.apple.com'],
  },
  {
    provider: 'Waze',
    description: 'Waze universal location links',
    hosts: ['waze.com'],
    pathname: /^\/ul(?:\/|$)/,
  },
  {
    provider: 'Waze',
    description: 'Waze app-scheme links',
    protocols: ['waze:'],
  },
  {
    provider: 'OpenStreetMap',
    description: 'OpenStreetMap full URLs',
    hosts: ['openstreetmap.org'],
  },
  {
    provider: 'OpenStreetMap',
    description: 'OpenStreetMap official shortlinks',
    hosts: ['osm.org'],
    pathname: /^\/go(?:\/|$)/,
  },
  {
    provider: 'Bing Maps',
    description: 'Bing Maps URLs',
    hosts: ['bing.com'],
    pathname: /^\/maps(?:\/|$)/,
  },
] as const

function matchesHost(hostname: string, domain: string): boolean {
  return hostname === domain || hostname.endsWith(`.${domain}`)
}

function matchesAnyHost(hostname: string, domains: readonly string[]): boolean {
  return domains.some((domain) => matchesHost(hostname, domain))
}

export function isAmazonHostname(hostname: string): boolean {
  const normalized = hostname.toLowerCase().replace(/^www\./, '')
  if (matchesAnyHost(normalized, AMAZON_SHORT_HOSTS)) return true
  const labels = normalized.split('.')
  const amazonLabel = labels.lastIndexOf('amazon')
  if (amazonLabel < 0) return false
  return AMAZON_STOREFRONT.test(labels.slice(amazonLabel).join('.'))
}

export function urlHostname(url: Pick<UrlEntity, 'domain' | 'canonicalUrl' | 'originalUrl'>): string {
  if (url.domain) return url.domain.toLowerCase().replace(/^www\./, '')
  try {
    return new URL(url.canonicalUrl ?? url.originalUrl).hostname.toLowerCase().replace(/^www\./, '')
  } catch {
    return ''
  }
}

function parsedPath(url: UrlEntity): string {
  try {
    return new URL(url.canonicalUrl ?? url.originalUrl).pathname.toLowerCase()
  } catch {
    return ''
  }
}

function parsedUrls(url: UrlEntity): URL[] {
  const candidates = [url.originalUrl, url.canonicalUrl]
  const parsed = new Map<string, URL>()
  for (const candidate of candidates) {
    if (!candidate) continue
    try {
      const value = new URL(candidate)
      parsed.set(value.href, value)
    } catch {
      // The URL may be temporarily unresolved or invalid; another representation
      // can still identify the preview safely.
    }
  }
  return [...parsed.values()]
}

function matchesMapUrlPattern(value: URL, pattern: MapUrlPattern): boolean {
  if (pattern.protocols && !pattern.protocols.includes(value.protocol.toLowerCase())) return false
  const hostname = value.hostname.toLowerCase().replace(/^www\./, '')
  if (pattern.hosts && !matchesAnyHost(hostname, pattern.hosts)) return false
  return !pattern.pathname || pattern.pathname.test(value.pathname.toLowerCase())
}

export function mapUrlPattern(url: UrlEntity): MapUrlPattern | null {
  for (const value of parsedUrls(url)) {
    const pattern = MAP_URL_PATTERNS.find((candidate) => matchesMapUrlPattern(value, candidate))
    if (pattern) return pattern
  }
  return null
}

export function mapProviderName(url: UrlEntity): MapUrlPattern['provider'] | null {
  return mapUrlPattern(url)?.provider ?? null
}

function isDocument(hostname: string, path: string): boolean {
  if (matchesHost(hostname, 'docs.google.com')) {
    return /^\/(document|spreadsheets|presentation|forms)\//.test(path)
  }
  return matchesAnyHost(hostname, [
    'drive.google.com',
    'notion.site',
    'notion.so',
    'figma.com',
    'canva.com',
    'dropbox.com',
    'box.com',
  ])
}

function isPlace(url: UrlEntity, hostname: string): boolean {
  return mapUrlPattern(url) !== null || matchesAnyHost(hostname, PLACE_HOSTS)
}

/** A recipe by its site, its address (/recipes/…, /rezepte/…, /מתכונים/…) or its title ("Pumpkin Bread Recipe", "המתכון הקל…"). */
export function isRecipe(url: Pick<UrlEntity, 'originalUrl' | 'canonicalUrl' | 'domain' | 'title'>, hostname = urlHostname(url)): boolean {
  if (matchesAnyHost(hostname, RECIPE_HOSTS)) return true
  let path = ''
  try { path = decodeURIComponent(new URL(url.canonicalUrl ?? url.originalUrl).pathname) } catch { /* an address that does not decode says nothing */ }
  const words = (text: string, split: RegExp) => text.toLowerCase().split(split).some((word) => RECIPE_WORD.test(word))
  if (words(path, /[/\-_.+]+/)) return true
  return Boolean(url.title && !FIGURATIVE_RECIPE.test(url.title) && words(url.title, /[^\p{L}]+/u))
}

function isProduct(hostname: string, path: string, contentType?: ContentType): boolean {
  if (contentType === 'product') return true
  return isAmazonHostname(hostname)
    || /(^|\.)(bestbuy|ebay|etsy|ikea|walmart)\./.test(hostname)
    || hostname.startsWith('shop.')
    || hostname.startsWith('store.')
    || /\/(products?|shop|store|dp)\//.test(path)
}

function fallbackCategory(contentType?: ContentType): UrlContentCategory {
  if (contentType === 'video') return 'video'
  if (contentType === 'product') return 'product'
  if (contentType === 'article') return 'article'
  return 'link'
}

/** Host- and path-only classification for a bare URL, before any page metadata exists. */
export function urlContentCategoryForHref(href: string): UrlContentCategory {
  let domain: string | null = null
  try { domain = new URL(href).hostname.toLowerCase() } catch { /* unparseable stays a link */ }
  return urlContentCategory({
    originalUrl: href, canonicalUrl: href, domain, title: null, description: null,
    imageUrl: null, durationSeconds: null, siteName: null, author: null, publishedAt: null,
    fetchedAt: null, metadataPolicyVersion: null, metadataFetchAttemptCount: 0,
    metadataNextRetryAt: null,
  })
}

export function urlContentCategory(
  url: UrlEntity,
  contentType?: ContentType,
): UrlContentCategory {
  const hostname = urlHostname(url)
  const path = parsedPath(url)

  if (matchesAnyHost(hostname, MOVIE_HOSTS)) return 'movie'
  if (matchesAnyHost(hostname, AUDIO_HOSTS)) return 'audio'
  if (matchesAnyHost(hostname, VIDEO_HOSTS)) return 'video'
  if (matchesAnyHost(hostname, SOCIAL_HOSTS)) return 'social'
  if (matchesAnyHost(hostname, BOOK_HOSTS)) return 'book'
  if (matchesAnyHost(hostname, EVENT_HOSTS)) return 'event'
  if (matchesAnyHost(hostname, REFERENCE_HOSTS)) return 'reference'
  if (isDocument(hostname, path)) return 'document'
  if (matchesAnyHost(hostname, CODE_HOSTS)) return 'code'
  if (isPlace(url, hostname)) return 'place'
  // A category declared by the page itself (schema.org / Open Graph) or judged by Jev
  // outranks path guesses and the weak author/date article hint below. A recipe is the
  // exception to "article": most recipe blogs declare themselves articles.
  if (isPageKind(url.pageKind) && url.pageKind !== 'article') return url.pageKind
  if (isRecipe(url, hostname)) return 'recipe'
  if (url.pageKind === 'article') return 'article'
  if (isProduct(hostname, path, contentType)) return 'product'
  if (url.author || url.publishedAt) return 'article'
  return fallbackCategory(contentType)
}

const CATEGORY_TAGS: Record<UrlContentCategory, readonly string[]> = {
  movie: ['movie', 'watch'],
  audio: ['song', 'listen'],
  video: ['video', 'watch'],
  social: ['social-media'],
  book: ['book', 'read'],
  event: ['event'],
  reference: ['reference', 'read'],
  document: ['document'],
  article: ['article', 'read'],
  product: ['shopping', 'buy'],
  place: ['map'],
  code: ['code'],
  listing: ['listing'],
  recipe: ['recipe', 'cook'],
  job: ['job'],
  link: ['link'],
}

export function suggestedTagsForUrlCategories(
  categories: readonly UrlContentCategory[],
): string[] {
  return [...new Set(categories.flatMap((category) => CATEGORY_TAGS[category]))]
}
